import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DefaultLayout from '../../layout/DefaultLayout';
import Loading from '../../components/Loading';
import paymentService from '../../services/paymentService';
import { useGlobalState } from '../../state';
import { toCurrency } from '../../utils/formaters';
import { FaFileAlt } from 'react-icons/fa';
import Alert from '../../components/Alert';
import Breadcrumb from '../../components/Breadcrumbs/Breadcrumb';

const Movimientos: React.FC = () => {
  const navigate = useNavigate();
  const [user] = useGlobalState('user');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [pagosRealizados, setPagosRealizados] = useState<any[] | undefined>(undefined);
  const [pagosPendientes, setPagosPendientes] = useState<any[] | undefined>(undefined);
  const [confirmados, setConfirmados] = useState<boolean>(true);

  useEffect(() => {
    // Si no hay usuario, redirigir a login
    if (user === undefined) return; // todavía verificando
    if (!user) {
      navigate('/auth/signin');
      return;
    }

    setLoading(true);
    paymentService.getUltimosPagos().then((result: any) => {
      if (result.success) {
        setPagosRealizados(result.data?.pagosRealizados ?? []);
        setPagosPendientes(result.data?.pagosPendientes ?? []);
      } else {
        setError(result.errors?.[0] ?? 'Ocurrió un error');
      }
    }).catch((ex: any) => {
      setError(ex?.message ?? 'Ocurrió un error');
    }).finally(() => setLoading(false));
  }, [user, navigate]);

  const getMetadataDescripcion = (metadata: any) => {
    if (!metadata) return '';
    try {
      if (typeof metadata === 'string') {
        const parsed = JSON.parse(metadata);
        return parsed?.descripcion ?? parsed?.Descripcion ?? '';
      }
      return metadata?.descripcion ?? metadata?.Descripcion ?? '';
    } catch {
      return '';
    }
  };

  const formatTipoPago = (tipoPago: any) => {
    if (!tipoPago) return undefined;
    const t = String(tipoPago).toUpperCase();
    if (t.includes('PLAN')) return 'Plan de pago';
    if (t.includes('TRANSFER')) return 'Transferencia';
    return 'MercadoPago';
  };

  const estadoClass = (estado: string | undefined) => {
    if (!estado) return 'bg-yellow-50 border-yellow-400';
    const e = String(estado).toUpperCase();
    if (e.includes('IMPUT') || e.includes('IMPUTADO')) return 'bg-green-50 border-green-500';
    if (e.includes('RECH') || e.includes('RECHAZADO')) return 'bg-red-50 border-red-500';
    return 'bg-yellow-50 border-yellow-400';
  };

  const TransactionItem: React.FC<any> = ({ periodo, concepto, monto, fecha_pago, estado, tipoPago }) => {
    return (
      <div className="flex items-start py-3 border-b border-gray-2">
        <div className="w-[70px] flex items-center">
          <div className={`rounded-full h-11 w-11 flex items-center justify-center border ${estadoClass(estado)}`}>
             <FaFileAlt className="text-boxdark-2" />
          </div>
        </div>

        <div className="w-2/3">
          <div className="text-sm md:text-xl font-semibold text-gray-800">{concepto}</div>
          <div className="text-xs md:text-sm text-gray-500">{periodo}</div>
          {tipoPago && <div className="text-xs text-gray-400">{formatTipoPago(tipoPago)}</div>}
        </div>

        <div className="w-1/6 text-right">
          <div className="text-sm md:text-xl font-bold text-gray-800">{toCurrency(monto)}</div>
          <div className="text-xs md:text-sm text-gray-500">{fecha_pago ? new Date(fecha_pago).toLocaleDateString() : ''}</div>
        </div>
      </div>
    );
  };

  return (
      <DefaultLayout>
          <Breadcrumb pageName="Movimientos" />

      {error && <Alert type="failure" message={error} />}

      <div className="md:p-6">
        <div className="md:max-w-6xl mx-auto bg-white md:p-6 rounded">

          {loading && <Loading statusText="Cargando" />}          

          <div className="flex border-b border-gray-2 mb-4">
            <button
              onClick={() => setConfirmados(true)}
                          className={`w-1/2 text-center py-2 font-semibold ${confirmados ? 'text-primario-celeste-800 border-b-2 border-primario-celeste-800' : 'text-gray-500'}`}
            >
              Confirmados
            </button>
            <button
              onClick={() => setConfirmados(false)}
                          className={`w-1/2 text-center py-2 font-semibold ${!confirmados ? 'text-primario-celeste-800 border-b-2 border-primario-celeste-800' : 'text-gray-500'}`}
            >
              En revisión
            </button>
          </div>

          {!loading ? (
            <>
              <div className="text-xs text-gray-500 mb-2">Los más recientes</div>

              <div className="bg-gray-50 rounded p-3">
                {confirmados ? (
                  pagosRealizados && pagosRealizados.length > 0 ? (
                    pagosRealizados.map((item: any) => (
                      <TransactionItem
                        key={item.idPago ?? item.id ?? `${item.fechaCreacion}-${item.monto}`}
                        periodo={item.tipoPago && String(item.tipoPago).toUpperCase().includes('PLAN') ? 'Plan de pago' : item.periodo}
                        concepto={getMetadataDescripcion(item.metadata)}
                        monto={item.monto}
                        fecha_pago={item.fechaCreacion}
                        estado={item.estado}
                        tipoPago={item.tipoPago}
                      />
                    ))
                  ) : (
                    <div className="text-center text-sm font-semibold text-gray-600 py-6">No hay pagos confirmados en los últimos meses</div>
                  )
                ) : (
                  pagosPendientes && pagosPendientes.length > 0 ? (
                    pagosPendientes.map((item: any) => (
                      <TransactionItem
                        key={item.idPago ?? item.id ?? `${item.fechaCreacion}-${item.monto}`}
                        periodo={item.tipoPago && String(item.tipoPago).toUpperCase().includes('PLAN') ? 'Plan de pago' : item.periodo}
                        concepto={getMetadataDescripcion(item.metadata)}
                        monto={item.monto}
                        fecha_pago={item.fechaCreacion}
                        estado={item.estado}
                        tipoPago={item.tipoPago}
                      />
                    ))
                  ) : (
                    <div className="text-center text-sm font-semibold text-gray-600 py-6">No hay pagos pendientes de revisión/imputación</div>
                  )
                )}
              </div>
            </>
          ) : null}
        </div>
        <div className="flex gap-3 justify-center mt-3">
            <button
                onClick={() => navigate(-1)}
                className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded"
            >
                Volver
            </button>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default Movimientos;